import { VV } from '@semios/app-platform-value-type-definitions'
import { IRRIGATION_ACTIVITY_FLOW_METER_VALUE_TYPE_PREFIX } from '@semios/app-platform-values-helpers-and-types'
import type { TValuesTimeseriesPoints } from 'App/Map/types'
import type {
  TBlockId,
  TIrrigationZoneEmitterId,
  TPoint,
  TProperty,
  TRegionId,
} from 'stores/fieldAssetStore/helperTypes'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { getClosestBlockInPropertyForAPoint } from 'utils/getClosestBlockInPropertyForAPoint'
import { getClosestIrrigationZoneEmitterInPropertyForAPoint } from 'utils/getClosestIrrigationZoneEmitterInPropertyForAPoint'
import { getClosestLngLatBasedFieldAssetInPropertyForAPoint } from 'utils/getClosestLngLatBasedFieldAssetInPropertyForAPoint'
import {
  POST_FILTER_PRESSURE_VALUE_TYPE,
  PRE_FILTER_PRESSURE_VALUE_TYPE,
} from 'utils/prePostFilterValueTypes'
import { getAnchorCoordinatesForBlock } from './by-field-asset/block'

const settings = VV.S

const valueGroupsForInBlockWeather = [
  'humidity',
  'dew_point',
  'wet_bulb',
  'atmospheric_pressure',
  'chill',
  'leaf_wetness',
  'air_temperature',
  'spray_conditions',
  'air_temperature',
  'weather_conditions',
]

const valueGroupsForOOBWeather = ['precipitation', 'wind']

const inBlockWeatherSettings = settings.weatherSettings.filter((s) =>
  valueGroupsForInBlockWeather.includes(s.valueTypeGroupId),
)

const ooBWeatherSettings = settings.weatherSettings.filter((s) =>
  valueGroupsForOOBWeather.includes(s.valueTypeGroupId),
)

const soilSettings = settings.soilSettings
const fruitGrowthSettings = settings.allSettings.filter((s) => s.valueTypeGroupId === 'fruit_growth')
const plantStressSettings = settings.allSettings.filter((s) => s.valueTypeGroupId === 'plant_stress')
const windMachineSettings = settings.allSettings.filter((s) => s.valueTypeGroupId === 'wind_machine')

export const getArgsForAnchorCoordinates = ({
  anchorCoordinates: anchorCoordsRaw,
  property,
  args,
}: {
  anchorCoordinates: { lng: number; lat: number }
  property: TProperty
  region?: TRegionId
  args?: {
    irrigationZoneEmitter?: TIrrigationZoneEmitterId
    block?: TBlockId
  }
}): Partial<TSelectedFieldAssetsStoreState> => {
  const closestIrrigationZoneEmitter = getClosestIrrigationZoneEmitterInPropertyForAPoint({
    anchorCoordinates: anchorCoordsRaw,
    property,
  })

  const irrigationZoneEmitter =
    args?.irrigationZoneEmitter ?? closestIrrigationZoneEmitter?.irrigationZoneEmitterId ?? null

  const block =
    args?.block ?? getClosestBlockInPropertyForAPoint({ anchorCoordinates: anchorCoordsRaw, property })

  const { primaryValueGroup } = selectedValueGroupsStore.getState()

  let anchorCoordinates = anchorCoordsRaw

  if (args?.block && args.block) {
    const blockAnchorCoords = getAnchorCoordinatesForBlock(args.block, property)

    anchorCoordinates = blockAnchorCoords ?? anchorCoordinates
  }

  const region = property.regionIds?.[0]

  return {
    property: property.propertyId,
    region,
    block,
    lngLat: getClosestLngLatBasedFieldAssetInPropertyForAPoint({
      anchorCoordinates,
      fieldAssetValues: Object.values(property.points || []),
      block,
    }),
    irrigationZoneEmitter,
    weatherPoint: getClosestLngLatBasedFieldAssetInPropertyForAPoint({
      anchorCoordinates,
      fieldAssetValues: getAllPointsForWeather(Object.values(property.points || {})),
      block,
    }),
    outOfBlockPoint: getClosestLngLatBasedFieldAssetInPropertyForAPoint({
      anchorCoordinates,
      fieldAssetValues: getAllPointsForOOBWeather(Object.values(property.points || {})),
      block,
    }),
    soilPoint: getClosestLngLatBasedFieldAssetInPropertyForAPoint({
      anchorCoordinates,
      fieldAssetValues: getAllLngLatsForSoil(Object.values(property.points || {})),
      block,
    }),
    fruitGrowthPoint: getClosestLngLatBasedFieldAssetInPropertyForAPoint({
      anchorCoordinates,
      fieldAssetValues: getAllLngLatsForFruitGrowth(Object.values(property.points || {})),
      block,
    }),
    plantStressPoint: getClosestLngLatBasedFieldAssetInPropertyForAPoint({
      anchorCoordinates,
      fieldAssetValues: getAllLngLatsForPlantStress(Object.values(property.points || {})),
      block,
    }),
    trapPoint: getClosestLngLatBasedFieldAssetInPropertyForAPoint({
      anchorCoordinates,
      fieldAssetValues: getAllPointsForTraps(Object.values(property.points || {}), primaryValueGroup),
      block,
    }),
    pressureTransducerPoint: getClosestLngLatBasedFieldAssetInPropertyForAPoint({
      anchorCoordinates,
      fieldAssetValues: getAllPointsForPressure(Object.values(property.points || {}), irrigationZoneEmitter),
    }),
    prePostFilterPoint: getClosestLngLatBasedFieldAssetInPropertyForAPoint({
      anchorCoordinates,
      fieldAssetValues: getAllPointsForPrePostFilter(Object.values(property.points ?? {})),
    }),
    windMachinePoint: getClosestLngLatBasedFieldAssetInPropertyForAPoint({
      anchorCoordinates,
      fieldAssetValues: getAllPointsForWindMachine(Object.values(property.points ?? {})),
      block,
    }),
    flowMeterDevice: getClosestLngLatBasedFieldAssetInPropertyForAPoint({
      anchorCoordinates,
      fieldAssetValues: getAllPointsForFlowMeter(Object.values(property.points ?? {})),
    }),
  }
}

const getAllPointsForWeather = (points: TPoint[]) => {
  return points.filter((p) =>
    inBlockWeatherSettings.some((v) => p.valuesTimeseries?.includes(v.id as TValuesTimeseriesPoints)),
  )
}

const getAllPointsForOOBWeather = (points: TPoint[]) => {
  return points.filter((p) =>
    ooBWeatherSettings.some((v) => p.valuesTimeseries?.includes(v.id as TValuesTimeseriesPoints)),
  )
}

const getAllLngLatsForSoil = (points: TPoint[]) => {
  return points.filter((p) =>
    soilSettings.some((v) => p.valuesTimeseries?.includes(v.id as TValuesTimeseriesPoints)),
  )
}

const getAllLngLatsForFruitGrowth = (points: TPoint[]) => {
  return points.filter((p) =>
    fruitGrowthSettings.some((v) => p.valuesTimeseries?.includes(v.id as TValuesTimeseriesPoints)),
  )
}

const getAllLngLatsForPlantStress = (points: TPoint[]) => {
  return points.filter((p) =>
    plantStressSettings.some((v) => p.valuesTimeseries?.includes(v.id as TValuesTimeseriesPoints)),
  )
}

const getAllPointsForTraps = (points: TPoint[], primaryValueGroup: string | null) => {
  const isInsectValueGroup = primaryValueGroup?.includes('insect_id')

  if (!isInsectValueGroup || !primaryValueGroup) {
    return points.filter((p) => p.configuration.trapInsectIds)
  }

  const [, insectId] = primaryValueGroup.split('insect_id_')

  return points.filter((p) => p.configuration.trapInsectIds?.includes(Number(insectId)))
}

const getAllPointsForPressure = (
  points: TPoint[],
  irrigationZoneEmitterId?: TIrrigationZoneEmitterId | null,
) => {
  return points.filter(
    (p) =>
      p.configuration.irrigationEmitterTypesAvailable &&
      irrigationZoneEmitterId &&
      p.irrigationEmitterZoneIds?.includes(irrigationZoneEmitterId),
  )
}

const getAllPointsForWindMachine = (points: TPoint[]) => {
  return points.filter((p) =>
    windMachineSettings.some((v) => p.valuesTimeseries?.includes(v.id as TValuesTimeseriesPoints)),
  )
}

export const getAllPointsForFlowMeter = (points: TPoint[]) => {
  return points.filter((p) =>
    p.valuesTimeseries.some((v) => v.includes(IRRIGATION_ACTIVITY_FLOW_METER_VALUE_TYPE_PREFIX)),
  )
}

export const getAllPointsForPrePostFilter = (points: TPoint[]) => {
  return points.filter(
    (p) =>
      !!p?.lngLat &&
      (p.configuration?.irrigationEmitterTypesAvailable?.some(
        (emitterType) => emitterType === 'pre-filter' || emitterType === 'post-filter',
      ) ||
        p.valuesTimeseries?.some(
          (v) => v === PRE_FILTER_PRESSURE_VALUE_TYPE || v === POST_FILTER_PRESSURE_VALUE_TYPE,
        )),
  )
}
